import type {
  AttemptResult,
  ChildProfile,
  Domain,
  DomainMastery,
  ProgressRepository,
  RewardState
} from "@/domain";

import {
  MAX_CHILD_PROFILES,
  PROFILE_EXPORT_SCHEMA_VERSION,
  ProfileLimitError,
  ProfileNotFoundError,
  createEmptyRewards,
  decodeProfileExport,
  encodeProfileExport,
  parseAttemptResult,
  parseChildProfile,
  parseMastery,
  parseRewardState,
  sortAttempts,
  sortMastery,
  sortProfiles
} from "./repository-codec";

export class InMemoryProgressRepository implements ProgressRepository {
  private profiles = new Map<string, ChildProfile>();
  private attempts = new Map<string, Map<string, AttemptResult>>();
  private mastery = new Map<string, Map<Domain, DomainMastery>>();
  private rewards = new Map<string, RewardState>();

  async listProfiles(): Promise<ChildProfile[]> {
    return sortProfiles(
      Array.from(this.profiles.values(), (profile) => parseChildProfile(profile))
    );
  }

  async getProfile(profileId: string): Promise<ChildProfile | undefined> {
    const profile = this.profiles.get(profileId);
    return profile === undefined ? undefined : parseChildProfile(profile);
  }

  async saveProfile(profile: ChildProfile): Promise<void> {
    const parsedProfile = parseChildProfile(profile);

    if (
      !this.profiles.has(parsedProfile.id) &&
      this.profiles.size >= MAX_CHILD_PROFILES
    ) {
      throw new ProfileLimitError();
    }

    this.profiles.set(parsedProfile.id, parsedProfile);
  }

  async deleteProfile(profileId: string): Promise<void> {
    const profiles = new Map(this.profiles);
    const attempts = new Map(this.attempts);
    const mastery = new Map(this.mastery);
    const rewards = new Map(this.rewards);

    profiles.delete(profileId);
    attempts.delete(profileId);
    mastery.delete(profileId);
    rewards.delete(profileId);

    this.profiles = profiles;
    this.attempts = attempts;
    this.mastery = mastery;
    this.rewards = rewards;
  }

  async listAttempts(profileId: string): Promise<AttemptResult[]> {
    const attempts = this.attempts.get(profileId);
    if (attempts === undefined) {
      return [];
    }

    return sortAttempts(
      Array.from(attempts.values(), (attempt) => parseAttemptResult(attempt))
    );
  }

  async saveAttempt(result: AttemptResult): Promise<void> {
    const attempt = parseAttemptResult(result);
    this.requireProfile(attempt.profileId);

    const profileAttempts = new Map(this.attempts.get(attempt.profileId));
    profileAttempts.set(attempt.id, attempt);
    this.attempts.set(attempt.profileId, profileAttempts);
  }

  async getMastery(profileId: string): Promise<DomainMastery[]> {
    const mastery = this.mastery.get(profileId);
    if (mastery === undefined) {
      return [];
    }

    return sortMastery(parseMastery(Array.from(mastery.values())));
  }

  async saveMastery(
    profileId: string,
    mastery: DomainMastery[]
  ): Promise<void> {
    this.requireProfile(profileId);
    const parsedMastery = parseMastery(mastery);
    this.mastery.set(
      profileId,
      new Map(parsedMastery.map((entry) => [entry.domain, entry]))
    );
  }

  async getRewards(profileId: string): Promise<RewardState> {
    const rewards = this.rewards.get(profileId);
    return parseRewardState(rewards ?? createEmptyRewards());
  }

  async saveRewards(profileId: string, rewards: RewardState): Promise<void> {
    this.requireProfile(profileId);
    this.rewards.set(profileId, parseRewardState(rewards));
  }

  async exportProfile(profileId: string): Promise<string> {
    const profile = parseChildProfile(this.requireProfile(profileId));
    const attempts = sortAttempts(
      Array.from(
        this.attempts.get(profileId)?.values() ?? [],
        (attempt) => parseAttemptResult(attempt)
      )
    );
    const mastery = sortMastery(
      parseMastery(Array.from(this.mastery.get(profileId)?.values() ?? []))
    );
    const rewards = parseRewardState(
      this.rewards.get(profileId) ?? createEmptyRewards()
    );

    return encodeProfileExport({
      schemaVersion: PROFILE_EXPORT_SCHEMA_VERSION,
      profile,
      attempts,
      mastery,
      rewards
    });
  }

  async importProfile(serialized: string): Promise<ChildProfile> {
    const data = decodeProfileExport(serialized);

    if (
      !this.profiles.has(data.profile.id) &&
      this.profiles.size >= MAX_CHILD_PROFILES
    ) {
      throw new ProfileLimitError();
    }

    const profiles = new Map(this.profiles);
    const attempts = new Map(this.attempts);
    const mastery = new Map(this.mastery);
    const rewards = new Map(this.rewards);

    profiles.set(data.profile.id, data.profile);
    attempts.set(
      data.profile.id,
      new Map(data.attempts.map((attempt) => [attempt.id, attempt]))
    );
    mastery.set(
      data.profile.id,
      new Map(data.mastery.map((entry) => [entry.domain, entry]))
    );
    rewards.set(data.profile.id, data.rewards);

    this.profiles = profiles;
    this.attempts = attempts;
    this.mastery = mastery;
    this.rewards = rewards;

    return parseChildProfile(data.profile);
  }

  async clearAll(): Promise<void> {
    this.profiles = new Map();
    this.attempts = new Map();
    this.mastery = new Map();
    this.rewards = new Map();
  }

  private requireProfile(profileId: string): ChildProfile {
    const profile = this.profiles.get(profileId);
    if (profile === undefined) {
      throw new ProfileNotFoundError(profileId);
    }
    return profile;
  }
}
